import React, { useState, useEffect, useCallback, useMemo } from "react";
import SubNav from "../../../shared/subnav/SubNav";
import CustomerInfoV2 from "./customerInfo/CustomerInfoV2";
import styles from "./OrderDetailsNewVersion.module.scss";
import HasPermissionTo from "../../../common/HasPermissionTo";
import { Link } from "react-router-dom";
import CancelOrderDialog from "../CancelOrderDialog";
import ProgressTracker from "./progressTracker/ProgressTracker";
import { withTranslation } from "react-i18next";
import CustomerReservationsClient from "../../../shared/clients/CustomerReservationsClient";
import { logMsg } from "../../../common/Logger";
import TradeIn from "../../tradeIn/TradeIn";
import WallBoxDetails from "./wallBoxDetails/WallBoxDetails";
import UpperCaseText from "../../../utils/UpperCaseText";
import CustomerOptOffline from "./customerOptOffline/CustomerOptOffline";
import OrderDetailsActionNotifications from "./orderDetailsActionNotifications/OrderDetailsActionNotifications";
import OrderInfo from "./orderInfo/OrderInfo";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import VehicleSpecs from "./vehicleSpecs/VehicleSpecs";
import DealerInformation from "../../customerHandling/orderDetails/DealerInformation";

function OrderDetailsNewVersion(props) {
  const [customerReservationDetails, setCustomerReservationDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [is400Error, setIs400Error] = useState(false);

  const dialogProps = useMemo(() => ({
    title: props.t("CustomerOrderDetails.confirmDialogTitle"),
    body: props.t("CustomerOrderDetails.bodyText"),
    checkbox: props.t("CustomerOrderDetails.checkBoxText"),
    checkboxList: [],
    cancel: props.t("CustomerOrderDetails.goBack"),
    confirm: props.t("CustomerOrderDetails.cancelBtnTxt"),
  }), [props.t]);

  const fetchCustomerOrderDetails = useCallback(() => {
    props.hideOrShow(true);
    setFetchFailed(false);
    const market = props.fromCrc ? props.selectedMarket : null;

    CustomerReservationsClient.getCustomerOrderDetails(
      props.user,
      props.reservationId,
      props.commonId,
      props.fromCrc,
      props.lang,
      market
    )
      .then((result) => {
        setCustomerReservationDetails(result);
        setIsLoading(false);
      })
      .catch((error) => {
        setFetchFailed(true);
        setIsLoading(false);
        logMsg("the error is: ", error);
      })
      .finally(() => {
        props.hideOrShow(false);
      });
  }, [props.hideOrShow, props.user, props.reservationId, props.commonId, props.fromCrc, props.lang, props.selectedMarket]);

  const handleUpdateOrder = useCallback((dealerAction, acceptedDate, cancelReason) => {
    props.hideOrShow(true);
    setOpenCancelDialog(false);

    const dealerActionInfo = {
      reservationId: props.reservationId,
      dealerAction: dealerAction,
      acceptedDate: acceptedDate,
      dealerRejectedReason: cancelReason,
    };

    CustomerReservationsClient.updateOrder(
      props.commonId,
      dealerActionInfo,
      props.user.userId,
      true
    )
      .then((result) => {
        if (result === "400") {
          setIs400Error(true);
        } else {
          fetchCustomerOrderDetails();
        }
      })
      .catch((error) => logMsg("the error is: ", error))
      .finally(() => {
        props.hideOrShow(false);
      });
  }, [props.hideOrShow, props.reservationId, props.commonId, props.user.userId, fetchCustomerOrderDetails]);

  const handleCancelClose = useCallback(() => {
    setOpenCancelDialog(false);
  }, []);

  const showCancelConfirmationPopup = useCallback((e) => {
    setOpenCancelDialog(true);
    e.stopPropagation();
  }, []);

  const isOrderCancelled = useCallback((status) => {
    return (
      status === "DEALER_CANCELLED" ||
      status === "RESERVATION_CANCELLED" ||
      status === "CONTRACT_CANCELLED" ||
      status === "ORDER_CANCELLED"
    );
  }, []);

  const isReservation = useCallback((orderType) => {
    return UpperCaseText(orderType) === "RESERVATION";
  }, []);

  const getSubNav = useCallback(() => {
    return (
      <SubNav
        dashboard={false}
        title={props.t("CustomerOrderDetails.orderDetail")}
        restrictDropdown={true}
        {...props}
      />
    );
  }, [props]);

  useEffect(() => {
    fetchCustomerOrderDetails();

    if (
      !props.lastPath ||
      !props.lastPath.includes("/customer-reservations")
    ) {
      const keys = Object.keys(sessionStorage);
      for (const key in keys) {
        const value = keys[key];
        if (
          value.startsWith("pageNumber_") ||
          value.startsWith("sortKey_") ||
          value.startsWith("sortDirection_") ||
          value.startsWith("sortByString_") ||
          value === "scrollPos"
        ) {
          sessionStorage.removeItem(value);
        }
      }
    }
  }, []);

  if (isLoading) {
    return getSubNav();
  }

  if (fetchFailed) {
    return (
      <div className={styles.errorText}>
        <h2>{props.t("FetchError.errorText")}</h2>
      </div>
    );
  }

  const customerReservation =
    customerReservationDetails && customerReservationDetails.customerReservation;
  const tradeIn =
    customerReservationDetails && customerReservationDetails.tradeIn;
  const customerInfo =
    customerReservationDetails && customerReservationDetails.customerInfo;
  const dealerInfo =
    customerReservationDetails && customerReservationDetails.dealerInfo;

  if (!customerReservation) {
    return (
      <>
        {getSubNav()}
        <div className="pagePadding">
          {props.t("CustomerOrderDetails.errorLoadingDetails")}
        </div>
      </>
    );
  }

  return (
    <div className="pageWrapper">
      {getSubNav()}

      <HasPermissionTo
        perform={["orderDetailsNotifications:view"]}
        permissions={props.user.permissions.rolePermissions}
        render={() => (
          <OrderDetailsActionNotifications
            {...props}
            customerReservation={customerReservation}
            tradeIn={tradeIn}
            isOrderCancelled={isOrderCancelled}
            isReservation={isReservation}
          />
        )}
      />

      <HasPermissionTo
        perform={["backToCRCSearchResults"]}
        permissions={props.user.permissions.rolePermissions}
        render={() => (
          <div className={styles.orderNumberHeader}>
            <button
              className={styles.back}
              onClick={() =>
                props.showOrderDetails(
                  props.commonDealerId,
                  null,
                  false
                )
              }
            >
              <KeyboardArrowLeftIcon fontSize="small" />
              <span className={styles.backToOrderText}>
                {props.t("CustomerOrderDetails.back")}
              </span>
            </button>
            <span className={styles.orderNumber}>
              {props.t("CustomerOrderDetails.orderId")}
              &nbsp; {customerReservation.id}
            </span>
          </div>
        )}
        noRender={() => (
          <div className={styles.orderNumberHeader}>
            <Link
              className={styles.back}
              id="back-button"
              to="/dealer/customer-reservations"
            >
              <KeyboardArrowLeftIcon fontSize="small" />
              <span className={styles.backToOrderText}>
                {props.t("CustomerOrderDetails.back")}
              </span>
            </Link>
            <span className={styles.orderNumber}>
              {props.t("CustomerOrderDetails.orderId")}
              &nbsp; {customerReservation.id}
            </span>
            <CancelOrderDialog
              dialogProps={dialogProps}
              isRefund={false}
              is400Error={is400Error}
              showDropdown={true}
              open={openCancelDialog}
              onClose={handleCancelClose}
              handleConfirm={handleUpdateOrder}
              token={props.user && props.user.token}
              {...props}
            />
          </div>
        )}
      />

      <div className={`customerOrderDetails ${styles.componentContainer}`}>
        <OrderInfo
          permissions={props.user.permissions.rolePermissions}
          customerReservation={customerReservation}
          showCancelConfirmationPopup={showCancelConfirmationPopup}
          market={
            props.fromCrc
              ? props.selectedMarket
              : props.user.market
          }
          defaultCurrency={
            props.localizedInfo &&
            props.localizedInfo.defaultCurrency &&
            props.localizedInfo.defaultCurrency.symbol
          }
        />
        <CustomerInfoV2
          customerInfoData={customerInfo}
          market={
            props.fromCrc
              ? props.selectedMarket
              : props.user.market
          }
        />
        {props.fromCrc && (
          <DealerInformation
            {...props}
            isNAMarket={false}
            dealerInfo={dealerInfo}
          />
        )}
        <VehicleSpecs
          {...props}
          customerInfoData={customerInfo}
          customerReservation={customerReservation}
          defaultCurrency={
            props.localizedInfo.defaultCurrency.symbol
          }
          offlinePricingUpdate={
            customerReservation.offlinePricingUpdate
              ? customerReservation.offlinePricingUpdate
              : {}
          }
          customerReservationStatus={customerReservation.status}
          pricingStack={customerReservation.pricingStack}
          tradeIn={tradeIn}
          market={
            props.fromCrc
              ? props.selectedMarket
              : props.user.market
          }
          nameplate={customerReservation.nameplate}
        />
      </div>

      <HasPermissionTo
        perform={["progressTracker:view"]}
        permissions={props.user.permissions.rolePermissions}
        render={() => (
          <div className={styles.componentContainer}>
            <ProgressTracker
              reservation={customerReservation}
              market={
                props.fromCrc
                  ? props.selectedMarket
                  : props.user.market
              }
            />
          </div>
        )}
      />

      <HasPermissionTo
        perform={["customerOptOffline:view"]}
        permissions={props.user.permissions.rolePermissions}
        condition={!isReservation(customerReservation.orderType)}
        render={() => (
          <div className={styles.componentContainer}>
            <CustomerOptOffline
              {...props}
              handleCustomerOptOffline={handleUpdateOrder}
              offlineDocInfo={
                customerReservation.offlineDocInfo
                  ? customerReservation.offlineDocInfo
                  : {}
              }
              isOrderCancelled={isOrderCancelled}
              customerReservation={customerReservation}
              offlinePricingUpdate={
                customerReservation.offlinePricingUpdate
                  ? customerReservation.offlinePricingUpdate
                  : {}
              }
            />
          </div>
        )}
      />

      <HasPermissionTo
        perform={["wallboxOrderDetails:view"]}
        permissions={props.user.permissions.rolePermissions}
        condition={
          !isReservation(customerReservation.orderType) &&
          customerReservation.chargingInstallationRequestDate
        }
        render={() => (
          <div className={styles.componentContainer}>
            <WallBoxDetails
              user={props.user}
              isOrderCancelled={isOrderCancelled}
              customerReservation={customerReservation}
            />
          </div>
        )}
      />

      <HasPermissionTo
        perform={["tradeIn:view"]}
        permissions={props.user.permissions.rolePermissions}
        condition={
          !isReservation(customerReservation.orderType) && tradeIn
        }
        render={() => (
          <div className={styles.componentContainer}>
            <TradeIn
              {...props}
              isOrderCancelled={isOrderCancelled}
              refreshOrderDetails={fetchCustomerOrderDetails}
              status={customerReservation.status}
              tradeInDetails={tradeIn}
            />
          </div>
        )}
      />
    </div>
  );
}

export default withTranslation("emp")(OrderDetailsNewVersion);
